import { translate } from "../components/locale/I18n.js";
import Layout from "../components/layout";
import LocaleContext from "../components/locale/LocaleContext.js";
import LocaleWrapper from "../components/locale/LocaleWrapper.js";
import PageTile from "../layout/PageTile.js";
import React from "react";
import ReactMarkdown from "react-markdown";
import withRoot from "../withRoot.js";

const ManagingDirector = `
Dr. Guillaume Zufferey    
Chemin de Bérée 22B   
1010 Lausanne 

+41 (0)76 516 86 71   
[guillaume@isyflow.ch](mailto:guillaume@isyflow.ch)
`;

const DataProtectionOfficer = `
Dr. Jan Peschka    
[privacy@isyflow.ch](mailto:privacy@isyflow.ch)
`;

const translations = {
    de: {
        PrivacyPolicy: `# Datenschutzerklärung
Diese Datenschutzerklärung soll Ihnen helfen zu verstehen, welche Informationen wir sammeln, warum wir sie sammeln und wie Sie Ihre Daten aktualisieren, verwalten, exportieren und löschen können.

## Informationen, die Isyflow sammelt
Wir sammeln Informationen, um allen unseren Nutzern bessere Dienstleistungen anbieten zu können, wie z.B. die Sprache, die Sie sprechen oder die Maßnahmen, die Sie auf der Plattform ergreifen.

### Personenbezogene Daten

Wenn Sie sich anmelden, geben Sie uns einige persönliche Daten wie Ihren Namen, Ihre E-Mail-Adresse und Ihre Sprache.

Ihr Name wird verwendet, um Sie über bekannte Personen zu informieren und andere darüber zu informieren, wer Inhalte auf der Plattform veröffentlicht, kommentiert oder erstellt hat.

Wir verwenden deine E-Mail-Adresse, damit Sie Ihr Passwort zurücksetzen können. Wir können Ihnen auch Benachrichtigungen über wichtige Ereignisse auf der Plattform zusenden, z.B. wenn jemand einen Ihrer Beiträge kommentiert hat oder wenn Ihnen eine Aktivität zugewiesen wurde. In den Benachrichtigungseinstellungen können Sie entscheiden, welche Art von Benachrichtigungen wir Ihr schicken.

### Inhalte, die Sie auf dieser Homepage deponieren

Wir sammeln die Inhalte, die Sie auf der Plattform erstellen (Beiträge, Aktivitäten, Kommentare, Aktivitätseinreichungen, Bewertungen) sowie die Benutzer, die Sie zu Ihren Arbeitsräume einladen.

Wir verwenden diese Informationen anonym, um die relative Bedeutung verschiedener Arten von Inhalten zu verstehen und mögliche Probleme im Zusammenhang mit dem kreativen Prozess zu identifizieren.

#### Ihre Aktivität

Wir erfassen Informationen darüber, was Sie auf Isyflow tun. Die von uns erfassten Aktivitätsinformationen können Folgendes beinhalten:
- Inhalte, die Sie gelesen haben
- Beiträge, die dir gefallen
- Beiträge, die Sie mit Ihrer Lerndokumentation verlinken.
- Personen, mit denen Sie kommunizieren oder Inhalte teilen

Diese Informationen werden anonym gespeichert und analysiert, um den Einsatz von Isyflow besser zu verstehen und die Plattform einfacher und effizienter zu gestalten.

## Verwaltung Ihrer Daten

Alle personenbezogenen Daten, die Isyflow sammelt, gehören Ihnen.

#### Änderung Ihrer persönlichen Informationen

Sie haben das Recht, Ihre persönlichen Daten zu berichtigen, wenn sie ungenau oder unvollständig sind. Bitte kontaktieren Sie uns unter support@isyflow.ch, wenn Sie Änderungen an Ihren Daten vornehmen möchten.

#### Beschwerden

Wenn Sie der Meinung sind, dass wir Ihre personenbezogenen Daten nicht ordnungsgemäß verarbeiten, haben Sie das Recht, bei einer Datenschutzbehörde Beschwerde einzureichen.

#### Löschung Ihrer persönlichen Informationen

Sie haben das Recht auf Löschung Ihrer personenbezogenen Daten, sofern keine gesetzlichen Aufbewahrungspflichten bestehen. Wenn Sie möchten, dass wir alle Ihre persönlichen Daten löschen, setzen Sie sich bitte unter support@isyflow.ch mit uns in Verbindung.

#### Weitere Rechte

Abgesehen von den oben genannten Rechten haben Sie auch das Recht auf Datenübertragbarkeit, das Recht auf Einschränkung der Verarbeitung und das Widerspruchsrecht gegen die Verarbeitung Ihrer personenbezogenen Daten. Bei Fragen zu diesen Rechten wenden Sie sich bitte an uns.

## Kontakt

Für weitere Fragen zu unserer Datenschutzpraxis oder wenn Sie Ihre Rechte in Bezug auf Ihre personenbezogenen Daten ausüben möchten, wenden Sie sich bitte an unseren Datenschutzbeauftragten.

#### Geschäftsführer und Verantwortlicher
${ManagingDirector}

#### Datenschutzbeauftragter
${DataProtectionOfficer}
`
    },
    en: {
        PrivacyPolicy: `# Privacy policy
This Privacy Policy is meant to help you understand what information we collect, why we collect it, and how you can update, manage, export, and delete your information.

## Information Isyflow collects
We collect information to provide better services to all our users, such as the language you speak or the actions you take on the platform.

### Personal information

You give us some personal information when you sign in, such as your name, your email address and your language.

Your name is used to let you find people you know  and let others know who posted/commented/created content on the platform.

We use your email address to let you reset your password. We may also send you notifications about important events on the platform, such as when someone commented on one of your posts or when an activity has been assigned to you. You can decide what kind of notifications we send you in the notification settings.


### Content that you upload on the platform

We collect the content that you create on the platform (posts, activities, documentations, comments, activity submissions, evaluations) as well as the users you invite to your workspaces.

We use this information anonymously to understand the relative importance of different types of content and identify potential problems related to the creative process.

### Your activity

We collect information about what you do on Isyflow. The activity information we collect may include:
- Content you read
- Posts you like
- Posts you link to your learning documentation
- People with whom you communicate or share content

This information is stored and analyzed anonymously, to better understand how Isyflow is used and to make the platform simpler and more efficient.

## Managing your data

All personal information Isyflow collects belongs to you.

#### Modification of your personal information

You have the right to correct your personal information if it is inaccurate or incomplete. Please contact us at support@isyflow.ch if you wish to make changes to your information.

#### Complaints

If you believe that we are not processing your personal information properly, you have the right to lodge a complaint with a data protection authority.

#### Deletion of your personal information

You have the right to have your personal information deleted, unless there is a legal obligation to retain it. If you would like us to delete all of your personal information, please contact us at support@isyflow.ch.

#### Other rights

Apart from the rights mentioned above, you also have the right to data portability, the right to restrict processing and the right to object to the processing of your personal data. If you have any questions about these rights, please contact us.

## Contact

For further questions about our data protection practices or if you wish to exercise your rights in relation to your personal data, please contact our Data Protection Officer.

#### Managing director and person in charge
${ManagingDirector}

#### Data protection officer
${DataProtectionOfficer}
    `
    },
    fr: {
        PrivacyPolicy: `# Politique de confidentialité
Cette politique de confidentialité a pour but de vous aider à comprendre quelles informations nous recueillons, pourquoi nous les recueillons et comment vous pouvez mettre à jour, gérer, exporter et supprimer vos informations.

## L'information que Isyflow recueille
Nous recueillons des informations pour fournir de meilleurs services à tous nos utilisateurs, tels que la langue que vous parlez ou les actions que vous entreprenez sur la plate-forme.

#### Renseignements personnels

Vous nous fournissez des renseignements personnels lorsque vous ouvrez une session, tels que votre nom, votre adresse électronique et votre langue.

Votre nom est utilisé pour vous permettre de trouver des personnes que vous connaissez et de faire savoir aux autres qui a posté/commenté/créé du contenu sur la plateforme.

Nous utilisons votre adresse e-mail pour vous permettre de réinitialiser votre mot de passe. Nous pouvons également vous envoyer des notifications à propos d'événements importants sur la plate-forme, par exemple lorsque quelqu'un a commenté l'un de vos messages ou lorsqu'une activité vous a été attribuée. Vous pouvez décider du type de notifications que nous vous envoyons dans les paramètres de notification.


#### Contenu que vous créez ou téléchargez sur la plateforme

Nous recueillons les contenus que vous créez sur la plateforme (messages, activités, commentaires, soumissions d'activités, évaluations) ainsi que les utilisateurs que vous invitez dans vos espaces de travail.

Nous utilisons ces informations de manière anonymisée pour comprendre l'importance relative des différents types de contenu et identifier les problèmes potentiels liés au processus de création.

#### Votre activité

Nous recueillons des informations sur ce que vous faites sur Isyflow. Les renseignements sur les activités que nous recueillons peuvent comprendre :
- Contenu que vous lisez
- Messages que vous aimez
- Posts que vous liez à votre documentation d'apprentissage
- Personnes avec qui vous communiquez ou partagez du contenu

Ces informations sont stockées et analysées de manière anonyme, pour mieux comprendre comment Isyflow est utilisée et pour rendre la plate-forme plus simple et plus efficace.

## Gérer vos données

Toutes les informations personnelles collectées par Isyflow vous appartiennent.

#### Modification de vos informations personnelles

Vous avez le droit de corriger vos données personnelles si elles sont inexactes ou incomplètes. Veuillez nous contacter à l'adresse support@isyflow.ch si vous souhaitez apporter des modifications à vos informations.

#### Plaintes

Si vous estimez que nous ne traitons pas correctement vos informations personnelles, vous avez le droit de déposer une plainte auprès d'une autorité de protection des données.

#### Suppression de vos informations personnelles

Vous avez le droit de demander la suppression de vos données à caractère personnel, à moins que la loi ne vous oblige à les conserver. Si vous souhaitez que nous supprimions toutes vos informations personnelles, veuillez nous contacter à l'adresse support@isyflow.ch.

#### Autres droits

Outre les droits susmentionnés, vous disposez également du droit à la portabilité des données, du droit à la limitation du traitement et du droit de vous opposer au traitement de vos données à caractère personnel. Pour toute question concernant ces droits, veuillez nous contacter.

## Contact

Pour toute autre question concernant nos pratiques en matière de protection des données ou si vous souhaitez exercer vos droits relatifs à vos données personnelles, veuillez contacter notre délégué à la protection des données.

#### Directeur général et responsable
${ManagingDirector}

#### Délégué à la protection des données
${DataProtectionOfficer}
    `
    }
};

const PrivacyPolicyPage = () => {
    return (
        <LocaleWrapper locale="en">
            <Layout>
                <PageTile verticalPadding={6}>
                    <LocaleContext.Consumer>
                        {({ locale }) => (
                            <ReactMarkdown skipHtml={false}>
                                {translate({
                                    label: "PrivacyPolicy",
                                    locale,
                                    translations
                                })}
                            </ReactMarkdown>
                        )}
                    </LocaleContext.Consumer>
                </PageTile>
            </Layout>
        </LocaleWrapper>
    );
};

export default withRoot(PrivacyPolicyPage);
